import { Fragment, useCallback, useEffect, useState } from "react"
import { useRouter } from "hooks/useRouter"
import { useSelector } from "react-redux"
import UserService from "services/user.service"
import { Loader } from "common/Loader"
import { ReceiptPaidStatusControl } from "components/ReceiptPaidStatusControl"
import {
    AddExpenseModal,
    EditExpenseModal,
    ImagePreviewModal,
    getOperationExpenses,
    getExpensesTotal,
    isImageFile,
} from "components/operations"
import { formatDate } from "utils/funcs"
import { useNotifications } from "hooks/useNotification"
import { userSelector } from "appRedux/selectors/user"
import { hasPermission } from "utils/permissions"
import { downloadFileUrl, formatMoney, myTripsPathWithPersistedTab, submissionDateDisplay } from "utils/evoTripsUi"

const service = new UserService()

export const OperationDetails = () => {
    const [operation, setOperation] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [previewFile, setPreviewFile] = useState<any>(null)
    const [showAddExpenseModal, setShowAddExpenseModal] = useState(false)
    const [showEditExpenseModal, setShowEditExpenseModal] = useState(false)
    const [editingExpense, setEditingExpense] = useState<any>(null)
    const [updatingPaidKey, setUpdatingPaidKey] = useState<string | null>(null)

    const { params, push } = useRouter()
    const operationId = params.id ?? ""
    const { successMessage, errorMessage } = useNotifications()

    const currentUser = useSelector(userSelector)
    const canEditPaid = hasPermission(currentUser, "receiptTransactions", "edit")

    const refreshOperation = useCallback(async () => {
        if (!operationId) return
        try {
            const res = await service.operationDetails(operationId)
            setOperation(res.data.data)
            setError(null)
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || "Failed to load operation")
            setOperation(null)
        }
    }, [operationId])

    useEffect(() => {
        if (!operationId) return
        setLoading(true)
        refreshOperation().finally(() => setLoading(false))
    }, [operationId, refreshOperation])

    const handleFileClick = (file: any) => {
        if (isImageFile(file.type)) {
            setPreviewFile(file)
        } else {
            window.open(file.url, "_blank")
        }
    }

    const expenses = operation ? getOperationExpenses(operation) : []
    const receiptsTotal = getExpensesTotal(expenses)

    const handleEditExpense = (expense: any) => {
        setEditingExpense(expense)
        setShowEditExpenseModal(true)
    }

    const handleDeleteExpense = async (expense: any) => {
        const expenseId = expense?._id?.toString?.() ?? expense?._id
        if (!operationId || !expenseId) {
            errorMessage("Cannot delete this receipt")
            return
        }
        if (
            !window.confirm(
                "Delete this receipt line? Attached files will be removed from storage."
            )
        ) {
            return
        }
        try {
            await service.deleteOperationExpense(String(expenseId), operationId)
            successMessage("Receipt deleted")
            await refreshOperation()
        } catch (e: any) {
            errorMessage(e?.response?.data?.message || "Failed to delete receipt")
        }
    }

    if (loading && !operation) {
        return <Loader />
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <p className="text-red-600 mb-4">Error: {error}</p>
                <button
                    type="button"
                    onClick={() => push(myTripsPathWithPersistedTab())}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Back to My Trips
                </button>
            </div>
        )
    }

    if (!operation) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <p className="text-gray-600 mb-4">Operation not found</p>
                <button
                    type="button"
                    onClick={() => push(myTripsPathWithPersistedTab())}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Back to My Trips
                </button>
            </div>
        )
    }

    return (
        <Fragment>
            <div className="flex flex-col mb-4">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="page-title">
                            Operation Details
                        </h1>
                    </div>
                    <button
                        type="button"
                        onClick={() => push(myTripsPathWithPersistedTab())}
                        className="px-4 py-2 bg-blue-600 text-white font-medium rounded hover:bg-blue-700 transition"
                    >
                        Back to My Trips
                    </button>
                </div>
            </div>

            <div className="card p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="field-label">Month</label>
                        <p className="mt-1 text-sm text-gray-900">{operation.monthName ?? "N/A"}</p>
                    </div>
                    <div>
                        <label className="field-label">Badge ID</label>
                        <p className="mt-1 text-sm text-gray-900">{operation.badgeId ?? "N/A"}</p>
                    </div>
                    <div>
                        <label className="field-label">Full Name</label>
                        <p className="mt-1 text-sm text-gray-900">{operation.fullName ?? "N/A"}</p>
                    </div>
                    <div>
                        <label className="field-label">Submission Date</label>
                        <p className={`mt-1 text-sm ${operation.submissionDate ? "text-gray-900" : "text-red-600 font-medium"}`}>
                            {operation.submissionDate ? submissionDateDisplay(operation.submissionDate) : "Incomplete"}
                        </p>
                    </div>
                    <div>
                        <label className="field-label">Monthly Allocation</label>
                        <p className="mt-1 text-lg font-semibold text-blue-600">
                            {formatMoney(operation.monthlyAllocation)}
                        </p>
                    </div>
                    <div>
                        <label className="field-label">Receipts Total</label>
                        <p className="mt-1 text-lg font-semibold text-green-600">
                            {formatMoney(operation.receiptsTotal ?? receiptsTotal)}
                        </p>
                    </div>
                    <div>
                        <label className="field-label">Approved Amount to Pay</label>
                        <p className="mt-1 text-lg font-semibold text-orange-600">
                            {formatMoney(operation.approvedAmountToPay)}
                        </p>
                    </div>
                </div>
            </div>

            <div className="card p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-medium text-gray-700">Receipts</h2>
                    <button
                        type="button"
                        onClick={() => setShowAddExpenseModal(true)}
                        className="px-3 py-1.5 bg-green-500 text-white text-sm font-medium rounded-md hover:bg-green-600 transition-colors"
                    >
                        Add receipt
                    </button>
                </div>

                {expenses.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No receipts for this operation</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {expenses.map((expense: any, index: number) => {
                            const expenseId =
                                expense._id?.toString?.() ?? String(expense._id ?? index)
                            const uk = `operations:${operationId}:${expenseId}`
                            const paid =
                                expense.receiptPaidStatus === "Paid" ||
                                    expense.receiptPaidStatus === "Not-Paid"
                                    ? expense.receiptPaidStatus
                                    : "Not-Paid"

                            return (
                                <div
                                    key={expense._id || index}
                                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative"
                                >
                                    <div className="absolute top-2 right-2 z-10 flex gap-1">
                                        <button
                                            type="button"
                                            onClick={() => handleEditExpense(expense)}
                                            className="text-blue-500 hover:text-blue-700 p-1.5 rounded-md hover:bg-blue-50 transition-colors bg-white shadow-sm"
                                            title="Edit receipt"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleDeleteExpense(expense)}
                                            className="text-red-500 hover:text-red-700 p-1.5 rounded-md hover:bg-red-50 transition-colors bg-white shadow-sm"
                                            title="Delete receipt"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                                            </svg>
                                        </button>
                                    </div>

                                    <div className="pr-16">
                                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700">
                                            <span>
                                                <span className="font-medium">Amount:</span>{" "}
                                                <span className="text-green-600 font-semibold">
                                                    ${parseFloat(expense.amount || 0).toFixed(2)}
                                                </span>
                                            </span>
                                            {expense.vendorName ? (
                                                <span>
                                                    <span className="font-medium">Vendor:</span> {expense.vendorName}
                                                </span>
                                            ) : null}
                                            {expense.city ? (
                                                <span>
                                                    <span className="font-medium">City:</span> {expense.city}
                                                </span>
                                            ) : null}
                                            {expense.transactionDate ? (
                                                <span>
                                                    <span className="font-medium">Date:</span>{" "}
                                                    {formatDate(expense.transactionDate)}
                                                </span>
                                            ) : expense.date ? (
                                                <span>
                                                    <span className="font-medium">Date:</span> {formatDate(expense.date)}
                                                </span>
                                            ) : null}
                                        </div>

                                        <div className="flex flex-wrap items-center gap-2 mt-3">
                                            <ReceiptPaidStatusControl
                                                value={paid}
                                                saving={updatingPaidKey === uk}
                                                disabled={!canEditPaid}
                                                onChange={
                                                    canEditPaid
                                                        ? async (next) => {
                                                            setUpdatingPaidKey(uk)
                                                            try {
                                                                await service.updateReceiptPaidStatus({
                                                                    source: "operations",
                                                                    parentId: operationId,
                                                                    expenseId: String(expenseId),
                                                                    receiptPaidStatus: next,
                                                                })
                                                                successMessage("Payment status updated")
                                                                await refreshOperation()
                                                            } catch (e: any) {
                                                                errorMessage(
                                                                    e?.response?.data?.message ||
                                                                    "Could not update payment status"
                                                                )
                                                            } finally {
                                                                setUpdatingPaidKey(null)
                                                            }
                                                        }
                                                        : undefined
                                                }
                                            />
                                        </div>
                                    </div>

                                    {expense.files && expense.files.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-200">
                                            <label className="text-sm font-medium text-gray-500 mb-2 block">
                                                Attachments ({expense.files.length})
                                            </label>
                                            <div className="flex flex-wrap gap-3">
                                                {expense.files.map((file: any) => (
                                                    <div
                                                        key={file._id}
                                                        className="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200 hover:bg-gray-100 transition"
                                                    >
                                                        {isImageFile(file.type) ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleFileClick(file)}
                                                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                                                            >
                                                                {file.fileName}
                                                            </button>
                                                        ) : (
                                                            <a
                                                                href={file.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                                                            >
                                                                {file.fileName}
                                                            </a>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            <AddExpenseModal
                showModal={showAddExpenseModal}
                setShowModal={setShowAddExpenseModal}
                operationId={operationId}
                onSuccess={() => refreshOperation()}
            />

            <EditExpenseModal
                showModal={showEditExpenseModal}
                setShowModal={setShowEditExpenseModal}
                operationId={operationId}
                expense={editingExpense}
                onSuccess={() => {
                    setEditingExpense(null)
                    setShowEditExpenseModal(false)
                    refreshOperation()
                }}
            />

            <ImagePreviewModal
                showModal={!!previewFile}
                setShowModal={(open) => !open && setPreviewFile(null)}
                imageUrl={previewFile?.url || ""}
                file={previewFile}
            />
        </Fragment>
    )
}
